import Tooltip from '@material-ui/core/Tooltip';
import Chip from '@material-ui/core/Chip';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import GS from '../grafsync/grafsync';
import Errored from './Errored';
import ExternalLinkDialog from './ExternalLinkDialog';
import InfoLinkDialog from './InfoLinkDialog';
import { globalStyles } from './style/gstheme';
import { makeCancelable } from './utils/utils';
import { Typography } from '@material-ui/core';

// IRIs that might be loaded but that should link externally
const EXTERNAL_BASE_IRIs = [
    "http://www.w3.org/2002/07/owl#",
    "http://www.w3.org/1999/02/22-rdf-syntax-ns#",
    "http://www.w3.org/2000/01/rdf-schema#",
    "http://www.w3.org/2001/XMLSchema#",
    "http://www.w3.org/ns/shacl#",
    "http://www.w3.org/2004/02/skos/core#",
    "http://www.w3.org/2008/05/skos-xl#",
    "http://www.w3.org/XML/1998/namespace"
];

const VALID_TYPES = ["Concept", "Label", "ConceptScheme", "Collection", "OrderedCollection",
    "Ontology", "OwlClass", "ObjectProperty", "DatatypeProperty", "AnnotationProperty", "NamedIndividual",
    "Dataset", "Resource"];

const styles = () => {
    return Object.assign({
        infoChip: {
            color: '#616161',
            maxWidth: '100%',
            border: 'solid 1px #9E9E9E',
            backgroundColor: 'transparent',
            '&:hover': {
                color: 'white'
            }
        },
        link: {
            color: 'black',
            maxWidth: '100%',
            backgroundColor: '#e8e8e8',
            '&:hover': {
                cursor: 'pointer'
            }
        }
    }, globalStyles);
};

class InfoLink extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            showInfoDetails: false,
            showExternalDetails: false,
            isErrored: false
        };
        this.cancellable = null;
    }

    showInfo = async () => {
        let idToUse = this.props.id;
        let exists = false;
        const isExternalIRI = EXTERNAL_BASE_IRIs.some(ebi => this.props.id.startsWith(ebi));
        if (!isExternalIRI && !GS.isLoaded(this.props.id, this.props.graph)) {
            try {
                await this.loadData(this.props.id).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                // If still not loaded might be an edited IRI so check for that
                if (!GS.isLoaded(this.props.id, this.props.graph)) {
                    const findData = await this.doFind({ value: this.props.id, graph: this.props.graph }).promise;
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    exists = findData && findData.exists;
                    if (exists && findData.loadValue && findData.value === this.props.id) {
                        idToUse = findData.loadValue;
                        await this.loadData(idToUse).promise;
                        if (this.cancellable && this.cancellable.isCancelled()) return;
                    }
                }
                else {
                    exists = true;
                }
            }
            catch (e) {
                this.setState({ isErrored: e });
            }
        }
        if (!isExternalIRI && GS.isLoaded(idToUse, this.props.graph)) {
            if (this.props.components && this.props.components.length > 0) {
                const data = GS.data(idToUse, this.props.graph);
                const typeList = Array.isArray(data.type) ? data.type : [data.type];
                const isValidType = VALID_TYPES.find(validType => typeList.includes(validType));
                if (isValidType) {
                    const linkList = data.inScheme ?
                        this.props.components
                            .filter(scheme => data.inScheme && data.inScheme.includes(scheme.id))
                            .map(scheme => scheme.id)
                        :
                        data.inDataset ?
                            this.props.components
                                .filter(dataset => data.inDataset && data.inDataset.includes(dataset.id))
                                .map(dataset => dataset.id)
                            :
                            data.isDefinedBy || data.visibility ?
                                this.props.components
                                    .filter(ont => {
                                        if (data.isDefinedBy && data.isDefinedBy.includes(ont.id)) return true;
                                        if (data.visibility && data.visibility === ont.id) return true;
                                        return false;
                                    })
                                    .map(ont => ont.id)
                                :
                                data.visibility ?
                                    this.props.components
                                        .filter(ont => data.visibility = ont.id)
                                        .map(ont => ont.id)
                                    :
                                    ["ConceptScheme", "Collection", "OrderedCollection", "Ontology", "Dataset"].includes(isValidType) ?
                                        [data.id]
                                        :
                                        [];
                    this.setState({ showInfoDetails: true, data, linkList, validType: isValidType });
                }
            }
        }
        else {
            this.setState({ showExternalDetails: true, noAccess: exists });
        }
    };

    loadData(id) {
        this.cancellable = makeCancelable(GS.loadData([id], {}, this.props.graph, true));
        return this.cancellable;
    }

    doFind(payload) {
        this.cancellable = makeCancelable(GS.find(payload));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    hideInfoDetails = () => this.setState({ showInfoDetails: false, showExternalDetails: false });

    clearError = () => this.setState({ isErrored: false });

    render() {

        const { classes, id, dataTest, graph, defaultLang, onToggleView } = this.props;
        const { showInfoDetails, linkList, data, validType, isErrored, showExternalDetails, noAccess } = this.state;

        if (isErrored) return <Errored data={isErrored} clearError={this.clearError} />;
        if (id.includes("graphifi")) return <Typography data-test="graphifi-link" variant="body2" style={{ fontSize: 13 }}>{id}</Typography>;

        return (
            <React.Fragment>
                <Chip
                    data-test={dataTest}
                    color="primary"
                    style={{ maxWidth: 400 }}
                    className={classes.infoChip}
                    onClick={this.showInfo}
                    label={<Tooltip
                        title={id}
                        placement="bottom-start"
                        PopperProps={{
                            "data-test": "chip-tip"
                        }}>
                        <span data-test="infolink-label">
                            {id}
                        </span>
                    </Tooltip>} />
                {showInfoDetails &&
                    <InfoLinkDialog
                        onClose={this.hideInfoDetails}
                        onToggleView={onToggleView}
                        linkList={linkList}
                        validType={validType}
                        data={data}
                        graph={graph}
                        defaultLang={defaultLang} />
                }
                {showExternalDetails &&
                    <ExternalLinkDialog
                        onClose={this.hideInfoDetails}
                        noAccess={noAccess}
                        id={id}
                    />
                }
            </React.Fragment>
        );
    }
}

InfoLink.propTypes = {
    dataTest: PropTypes.string,
    id: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    components: PropTypes.array,
    onToggleView: PropTypes.func,
    graph: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withRouter(withStyles(styles)(InfoLink));